/**
 * Assign a person to a project, or change an existing assignment (PRD §4.3).
 *
 * Entry date and R&D share live on the assignment, not on the person, so a second project can
 * start on a different day with a different share. Once hours hang off an assignment only
 * Grantonomy may still change it (PLAN.md D-22) — the server decides that and sends `canEdit`.
 * Active shares across projects must sum to ≤ the configured max (default 95%).
 */

import { useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/ui/date-field";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toastApiError } from "@/lib/rate-limit";
import { useAssign, useRdShareLimits, useUpdateAssignment } from "@/lib/tenancy";
import { berlinToday, entryDateMaxHorizon } from "@/shared/dates";
import {
  RD_SHARE_LIMITS_DEFAULT,
  defaultRdPercent,
  isRdSharePercentInRange,
  rdShareInputMaxPercent,
  type RdShareLimits,
} from "@/shared/rd-share-limits";
import {
  percentToShare,
  remainingRdShare,
  shareToPercent,
  type AssignmentSummary,
  type PersonSummary,
  type ProjectSummary,
} from "@/shared/schemas/tenancy";

export function AssignmentDialog({
  person,
  assignment,
  projects,
  onOpenChange,
}: {
  person: PersonSummary;
  /** `null` creates a new assignment; a value edits that one. */
  assignment: AssignmentSummary | null;
  projects: ProjectSummary[];
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const rdLimitsQuery = useRdShareLimits();

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t(assignment ? "employees.editAssignment" : "employees.assignTitle")}
          </DialogTitle>
          <DialogDescription>
            {assignment
              ? t("employees.editAssignmentBody")
              : t("employees.assignBody", {
                  name: `${person.firstName} ${person.lastName}`.trim(),
                })}
          </DialogDescription>
        </DialogHeader>

        {!rdLimitsQuery.isFetched ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <AssignmentDialogForm
            person={person}
            assignment={assignment}
            projects={projects}
            rdLimits={rdLimitsQuery.data ?? RD_SHARE_LIMITS_DEFAULT}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssignmentDialogForm({
  person,
  assignment,
  projects,
  rdLimits,
  onOpenChange,
}: {
  person: PersonSummary;
  assignment: AssignmentSummary | null;
  projects: ProjectSummary[];
  rdLimits: RdShareLimits;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const assign = useAssign();
  const update = useUpdateAssignment();
  const submitLock = useRef(false);

  const taken = new Set(
    person.assignments.filter((entry) => !entry.archived).map((entry) => entry.projectId),
  );
  const selectable = projects.filter((project) => !taken.has(project.id));

  const remainingShare = remainingRdShare(person.assignments, assignment?.id, rdLimits);
  const remainingPercent = shareToPercent(remainingShare);
  const rdInputMax = rdShareInputMaxPercent(remainingPercent, rdLimits);

  const [projectId, setProjectId] = useState(assignment?.projectId ?? "");
  const [entryDate, setEntryDate] = useState(assignment?.entryDate ?? berlinToday());
  const [leaveDate, setLeaveDate] = useState(assignment?.leaveDate ?? "");
  const [rdPercent, setRdPercent] = useState(
    assignment
      ? String(shareToPercent(assignment.rdShare))
      : String(defaultRdPercent(remainingPercent, rdLimits)),
  );
  const [sendInvite, setSendInvite] = useState(true);

  const selectedProject = projects.find(
    (project) => project.id === (assignment?.projectId ?? projectId),
  );
  const entryHorizon = entryDateMaxHorizon();
  const entryDateMax =
    selectedProject?.endDate && selectedProject.endDate < entryHorizon
      ? selectedProject.endDate
      : entryHorizon;
  const canEditCore = assignment ? assignment.canEdit : true;
  const canEditLeave = assignment ? assignment.canEditLeaveDate : true;
  const leaveOnly = Boolean(assignment && !assignment.canEdit && assignment.canEditLeaveDate);
  const busy = assign.isPending || update.isPending;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (submitLock.current || busy) return;
    if (assignment && !assignment.canEdit && !assignment.canEditLeaveDate) return;

    let rdShare = assignment?.rdShare ?? 0;
    if (!leaveOnly) {
      const percent = Number(rdPercent);
      if (!isRdSharePercentInRange(percent, rdLimits)) {
        toast.error(
          t("errors.RD_SHARE_OUT_OF_RANGE", {
            min: rdLimits.minPercent,
            max: rdLimits.maxPercent,
          }),
        );
        return;
      }
      rdShare = percentToShare(percent);
      if (!(rdShare > 0) || Number.isNaN(rdShare)) return;
      if (rdShare > remainingShare + 1e-9) {
        toast.error(
          t("errors.RD_SHARE_EXCEEDS_MAX", {
            remaining: remainingPercent,
            max: rdLimits.maxPercent,
          }),
        );
        return;
      }
      if (selectedProject && entryDate < selectedProject.startDate) {
        toast.error(
          t("errors.ASSIGNMENT_ENTRY_BEFORE_PROJECT_START", {
            project_start_date: selectedProject.startDate,
          }),
        );
        return;
      }
      if (selectedProject?.endDate && entryDate > selectedProject.endDate) {
        toast.error(
          t("errors.ASSIGNMENT_ENTRY_AFTER_PROJECT_END", {
            project_end_date: selectedProject.endDate,
          }),
        );
        return;
      }
      if (entryDate > entryHorizon) {
        toast.error(
          t("errors.ASSIGNMENT_ENTRY_TOO_FAR_IN_FUTURE", {
            horizon_date: entryHorizon,
          }),
        );
        return;
      }
    }

    if (leaveDate && leaveDate < entryDate) {
      toast.error(t("errors.ASSIGNMENT_LEAVE_BEFORE_ENTRY", { entry_date: entryDate }));
      return;
    }
    if (leaveDate && selectedProject?.endDate && leaveDate > selectedProject.endDate) {
      toast.error(
        t("errors.ASSIGNMENT_LEAVE_AFTER_PROJECT_END", {
          project_end_date: selectedProject.endDate,
        }),
      );
      return;
    }

    submitLock.current = true;
    const handlers = {
      onSuccess: () => {
        toast.success(t(assignment ? "employees.assignmentUpdated" : "employees.assigned"));
        onOpenChange(false);
      },
      onError: (error: unknown) => {
        submitLock.current = false;
        toastApiError(error);
      },
    };

    if (assignment) {
      if (assignment.canEdit) {
        update.mutate(
          { id: assignment.id, patch: { entryDate, rdShare, leaveDate: leaveDate || null } },
          handlers,
        );
      } else if (assignment.canEditLeaveDate) {
        update.mutate({ id: assignment.id, patch: { leaveDate: leaveDate || null } }, handlers);
      }
    } else {
      assign.mutate(
        {
          userId: person.id,
          assignment: {
            projectId,
            entryDate,
            rdShare,
            sendInvite,
            leaveDate: leaveDate || undefined,
          },
        },
        handlers,
      );
    }
  }

  return (
    // noValidate: custom toasts for RD% / entry-date (native max would hide remaining %).
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="assignment-project">{t("employees.assignProject")}</Label>
        {assignment ? (
          <Input id="assignment-project" value={assignment.projectName} disabled />
        ) : (
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger id="assignment-project">
              <SelectValue placeholder={t("employees.assignProject")} />
            </SelectTrigger>
            <SelectContent>
              {selectable.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {!assignment && selectable.length === 0 && (
          <p className="text-xs text-muted-foreground">{t("employees.noProjectsYet")}</p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="assignment-entry">{t("employees.assignEntryDate")}</Label>
          <DateField
            id="assignment-entry"
            value={entryDate}
            min={selectedProject?.startDate}
            max={entryDateMax}
            onChange={setEntryDate}
            required
            disabled={!canEditCore}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="assignment-leave">{t("employees.assignLeaveDate")}</Label>
          <DateField
            id="assignment-leave"
            value={leaveDate}
            min={entryDate}
            max={selectedProject?.endDate}
            onChange={setLeaveDate}
            disabled={!canEditLeave}
          />
          <p className="text-xs text-muted-foreground">{t("employees.assignLeaveDateHint")}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="assignment-rd">
            {t("employees.assignRdShare", {
              min: rdLimits.minPercent,
              max: rdLimits.maxPercent,
            })}
          </Label>
          <Input
            id="assignment-rd"
            type="number"
            min={rdLimits.minPercent}
            max={rdInputMax}
            step="1"
            value={rdPercent}
            onChange={(event) => setRdPercent(event.target.value)}
            required
            disabled={!canEditCore}
          />
          <div className="space-y-0.5" data-testid="assignment-rd-remaining">
            <p className="text-xs text-muted-foreground">
              {t("employees.assignRdRemaining", {
                max: defaultRdPercent(remainingPercent, rdLimits),
              })}
            </p>
            <p className="text-[11px] leading-snug text-muted-foreground/80">
              {t("employees.assignRdRemainingHint")}
            </p>
          </div>
        </div>
      </div>

      {!assignment && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={sendInvite}
            onCheckedChange={(checked) => setSendInvite(checked === true)}
            data-testid="assignment-send-invite"
          />
          {t("employees.sendInvite")}
        </label>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={
            busy ||
            (!assignment && !projectId) ||
            (Boolean(assignment) && !canEditCore && !canEditLeave)
          }
        >
          {t("common.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}
